import { Portal } from '@chakra-ui/react';
import { useFocus } from 'modules/Editor/components/Focus';
import { SIDE_MENU_WIDGET_TYPES } from 'modules/Editor/components/SideMenuSettings/SideMenuSettings.config';
import { useAppSelector } from 'modules/Editor/store';
import { selectIsWidgetSettingsView } from 'modules/Editor/store/editorSettingsSelector';
import { selectIsActiveWidget, selectIsActiveWidgetByGroup } from 'modules/Editor/store/widgetSelector';
import { ReactElement } from 'react';
import { isDevelopment } from 'utils/environment';
import { RESPONSIVE_WIDGET_LIST } from 'widgets/Widget.config';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

import { SideMenuSettingProvider } from './SideMenuSettingContext';
import { useWidget } from './useWidget';

interface WidgetSideMenuSettingsProps {
  children: React.ReactNode;
}

/**
 * WIDGET SIDE MENU SETTINGS COMPONENT
 *
 * Wrapper for right side panel settings.
 * Uses a portal to render the the side menu
 *
 */
export const WidgetSideMenuSettings = ({ children }: WidgetSideMenuSettingsProps): ReactElement | null => {
  const { widgetSideMenuSettingsPortalRef } = useFocus();
  const { widgetId } = useWidget();
  const isWidgetSelected = useAppSelector(selectIsActiveWidget(widgetId));
  const isWidgetSettingsView = useAppSelector(selectIsWidgetSettingsView);
  const isGroupWidgetSelected = useAppSelector(selectIsActiveWidgetByGroup(widgetId));
  const isResponsiveWidget = RESPONSIVE_WIDGET_LIST.includes(getWidgetTypeFromId(widgetId));

  const shouldCloseSideMenu = isResponsiveWidget
    ? !isGroupWidgetSelected || !isWidgetSettingsView
    : !isWidgetSelected || !isWidgetSettingsView;

  // Close settings view if there is no side setting view
  if (shouldCloseSideMenu) return null;

  if (isDevelopment) {
    const widgetType = getWidgetTypeFromId(widgetId);
    if (!SIDE_MENU_WIDGET_TYPES.includes(widgetType)) {
      console.warn(
        'You may have a bug ' +
          'that the side menu may be closed unexpectedly when switching betweeen charts having a side menu.' +
          ' Please add the widget type to the SIDE_MENU_WIDGET_TYPES array',
      );
    }
  }

  return (
    <Portal containerRef={widgetSideMenuSettingsPortalRef as React.RefObject<HTMLElement | null> | undefined}>
      <SideMenuSettingProvider>{children}</SideMenuSettingProvider>
    </Portal>
  );
};
